import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import {
    absoluteTimeInTimezoneLabel,
    absoluteTimeLabel,
    relativeTimeLabel,
} from '@apps/web/features/admin_agent_scratchpad/model/relative_time';
import { cn } from '@apps/web/lib/utils';

/**
 * Relative label by default; hover surfaces the absolute UTC and, when the
 * note is organization-scoped, the organization's local time so vault
 * readers don't have to translate dates from another timezone in their head.
 * Trigger is a real focusable button so keyboard/screen-reader users can
 * reach the tooltip.
 */
export function RelativeTimestamp({
    iso,
    prefix,
    className,
    organizationTimezone,
}: {
    readonly iso: string;
    readonly prefix?: string;
    readonly className?: string;
    readonly organizationTimezone?: string | null;
}) {
    const utcLabel = absoluteTimeLabel(iso);
    const orgLabel =
        organizationTimezone !== undefined && organizationTimezone !== null
            ? absoluteTimeInTimezoneLabel(iso, organizationTimezone)
            : null;
    return (
        <Tooltip>
            <TooltipTrigger asChild={true}>
                <button
                    type="button"
                    className={cn(
                        'text-muted-foreground hover:text-foreground rounded-sm tabular-nums focus:outline-none focus-visible:ring-1 focus-visible:ring-offset-1',
                        className,
                    )}
                >
                    {prefix ? `${prefix} ` : ''}
                    {relativeTimeLabel(iso)}
                </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
                <div className="flex flex-col gap-0.5">
                    <span className="font-mono tabular-nums">{utcLabel}</span>
                    {orgLabel !== null ? (
                        <span className="text-muted-foreground font-mono tabular-nums">
                            {orgLabel}
                        </span>
                    ) : null}
                </div>
            </TooltipContent>
        </Tooltip>
    );
}
